import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { MERCHANT_ENTERPRISE_SERVICE, fetchBrand, fetchBrandVenues, fetchVenues } from 'apis/enterprise/api';
import { ExperienceEntityTree, ExperienceEntity, ExperienceEntityType } from 'modules/experience-core';

async function fetchVenuesData(venuesIds: string[]) {
  const { results } = await fetchVenues(venuesIds);
  return results;
}

export const useBrand = (identifier: string, isBrandFetchEnabled = true) => {
  const {
    data: brand,
    isLoading: isBrandLoading,
    error: errorBrand,
  } = useQuery(
    [`/v1/brands/${identifier}`],
    () => {
      return identifier ? fetchBrand(identifier) : undefined;
    },
    {
      refetchOnWindowFocus: false,
      enabled: isBrandFetchEnabled,
    },
  );

  const {
    data: brandVenues,
    isLoading: isBrandVenuesLoading,
    error: errorBrandVenues,
    isSuccess: onSuccessBrandVenues,
  } = useQuery([`${MERCHANT_ENTERPRISE_SERVICE}/brands/${identifier}/venues`], () => fetchBrandVenues(identifier), {
    refetchOnMount: false,
    enabled: isBrandFetchEnabled,
  });

  const venueIds = brandVenues?.map(item => item.venueId) || [];

  const {
    data: venues = [],
    isLoading: isVenuesLoading,
    error: errorVenues,
  } = useQuery({
    queryKey: ['fetchVenuesData', venueIds],
    queryFn: () => fetchVenuesData(brandVenues?.map(item => item.venueId) || []),
    enabled: onSuccessBrandVenues && isBrandFetchEnabled,
  });

  const isLoading = isBrandLoading || isBrandVenuesLoading || isVenuesLoading;
  const experienceEntity: ExperienceEntity | null = useMemo(() => {
    if (!brand || isLoading) {
      return null;
    }
    return {
      experienceEntityId: brand.id,
      experienceEntityType: ExperienceEntityType.BRAND,
      experienceEntityObject: brand,
    };
  }, [brand, isLoading]);

  const experienceEntityTree: ExperienceEntityTree | null = useMemo(() => {
    if (isLoading || !experienceEntity || !brandVenues || !venues) {
      return null;
    }
    return {
      experienceEntity,
      children: venues.map(
        venue =>
          ({
            experienceEntity: {
              experienceEntityId: venue.id,
              experienceEntityType: ExperienceEntityType.VENUE,
              experienceEntityObject: venue,
            },
            children: [],
          }) as const,
      ),
    };
  }, [isLoading, experienceEntity, brandVenues, venues]);

  return {
    experienceEntity,
    experienceEntityTree,
    error: errorBrand || errorBrandVenues || errorVenues,
    isLoading,
  };
};
